import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/authContext';
import useFrontendUsage from '../../hooks/useFrontendUsage';
import { PUBLIC_PATHS } from '../../util/frontendMigration';
import { resolveViewTitle } from '../../util/viewTitle';

// Heartbeats measure active time per frontend; keep in sync with the
// heartbeatIntervalMinutes the statistics endpoint reports.
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;

// Headless tracker feeding the migration statistics: one pageview event per
// route change and one heartbeat every few minutes while the tab is visible.
// Tracking failures are ignored — telemetry must never disturb the user.
function FrontendUsageTracker() {
  const auth = useAuth();
  const location = useLocation();
  const { trackEvent } = useFrontendUsage();
  const trackEventRef = useRef(trackEvent);
  trackEventRef.current = trackEvent;

  // Pageview on every route change while logged in.
  useEffect(() => {
    if (!auth.token || PUBLIC_PATHS.has(location.pathname)) return;
    const viewTitle = resolveViewTitle({
      pathname: location.pathname,
      menuItems: auth.menuItems,
    });
    trackEventRef.current({
      eventType: 'pageview',
      path: location.pathname,
      viewTitle: viewTitle || undefined,
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-track on login state or route changes
  }, [auth.token, location.pathname]);

  // Heartbeat while the tab is visible.
  useEffect(() => {
    if (!auth.token) return;
    const interval = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      const pathname = window.location.pathname;
      if (PUBLIC_PATHS.has(pathname)) return;
      trackEventRef.current({ eventType: 'heartbeat', path: pathname }).catch(() => {});
    }, HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [auth.token]);

  return null;
}

export default FrontendUsageTracker;
